import { ProgramStatusToggle } from './ProgramStatusToggle/ProgramStatusToggle'
import { ConfigurableButton, ConfigurableText } from 'app-components'
import { EnrollErrorMessage } from 'features/utility-programs/UtilityProgramEnrollmentTogglePrompt/EnrollErrorMessage'
import {
  EnrollmentRoot,
  UtilityProgram,
} from '@/types/programs-new/programs-new'
import { UtilityProgramViewConfig } from '@/types/utilityProgram'
import { useState } from 'react'
import { DeviceEnrollment } from '@/types/utilityProgramEnrollment'
import { logEvent } from '@/logging'
import { useNavigation } from '@/app/hooks'
import { DeviceType } from '@/app/components/DeviceSelectionGallery/DeviceSelectionGallery.types'

export type ProgramToggleOptions = {
  includeHeader?: boolean
  controlMode?: 'toggle' | 'button'
}

type Props = {
  deviceEnrollment: EnrollmentRoot | null
  viewConfig: UtilityProgramViewConfig
  utilityProgram: UtilityProgram
  options?: ProgramToggleOptions
  onUnenroll?: () => void
  deviceId: string | number
  deviceType: DeviceType
}

export function useViewModel({
  viewConfig,
  utilityProgram,
  onUnenroll,
}: Props) {
  const [enrollmentError, setEnrollmentError] = useState<string | null>(null)
  const navigate = useNavigation()

  const logNewEnrollment = (enrollment: DeviceEnrollment) => {
    if (!enrollment.unenrolled_at) {
      logEvent('utilityEnrollment', {
        metadata: { program: utilityProgram?.name ?? 'undefined' },
      })
    } else {
      onUnenroll?.()
    }
  }

  const onButtonClick = () => {
    navigate.push('/utility-program-enrollment')
  }

  return {
    cardConfig: viewConfig?.scheduler,
    logNewEnrollment,
    setEnrollmentError,
    onButtonClick,
    enrollmentError,
  }
}

const DEFAULT_OPTIONS: ProgramToggleOptions = {
  includeHeader: true,
  controlMode: 'toggle',
}

export default function UtilityProgramEnrollmentToggleContainer(props: Props) {
  const {
    cardConfig,
    logNewEnrollment,
    enrollmentError,
    setEnrollmentError,
    onButtonClick,
  } = useViewModel(props)

  const logoUrl = props.viewConfig.logoUrl

  const options = props.options ?? DEFAULT_OPTIONS

  return (
    <div>
      {enrollmentError && (
        <div className="mb-4">
          <EnrollErrorMessage error={enrollmentError} />
        </div>
      )}
      {options?.includeHeader && (
        <ConfigurableText variant="h3" config={cardConfig.title} />
      )}
      <div className="flex justify-between items-center my-4 w-full max-w-desktop mx-auto">
        <div className="w-[152px] h-[30px] flex items-center">
          {logoUrl && (
            <img
              src={logoUrl}
              alt="Utility Program Logo"
              className="max-h-full max-w-full object-contain"
            />
          )}
        </div>
        {options?.controlMode !== 'button' && (
          <div className="align-items-end">
            <ProgramStatusToggle
              enrollment={props.deviceEnrollment}
              onSuccess={logNewEnrollment}
              onError={setEnrollmentError}
              viewConfig={props.viewConfig}
              utilityProgramId={props.utilityProgram.id}
              deviceId={props.deviceId}
              deviceType={props.deviceType}
            />
          </div>
        )}
      </div>
      <ConfigurableText variant="body4" config={cardConfig.description}>
        Control whether or not you wish to participate in this program.
      </ConfigurableText>
      {options?.controlMode === 'button' && (
        <div className="mt-4">
          <ConfigurableButton
            id="charge-schedule-enrollment-prompt-button"
            variant="primary"
            onClick={onButtonClick}
            config={props.viewConfig.scheduler.button}
          >
            Get Started
          </ConfigurableButton>
        </div>
      )}
    </div>
  )
}
